import { createContext, useContext, useState, useCallback, useEffect } from "react";
import { useUserAuth } from "./UserAuthContext";
import { toast } from "sonner";

const GUEST_KEY = "epp_guest_cart";
const CartContext = createContext(null);

function readGuest() {
  try { return JSON.parse(localStorage.getItem(GUEST_KEY)) || []; }
  catch { return []; }
}
function writeGuest(items) {
  localStorage.setItem(GUEST_KEY, JSON.stringify(items));
}

export function CartProvider({ children }) {
  const { isAuthenticated, api } = useUserAuth();
  const [items, setItems] = useState(() => (isAuthenticated ? [] : readGuest()));
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);

  const count = items.reduce((s, i) => s + (i.quantity || 1), 0);
  const subtotal = items.reduce((s, i) => s + (Number(i.unit_price || i.price || 0) * (i.quantity || 1)), 0);

  const fetchServer = useCallback(async () => {
    if (!isAuthenticated || !api) return;
    setLoading(true);
    try {
      const res = await api("/cart");
      if (res.ok) {
        const data = await res.json();
        setItems(data.items || []);
      }
    } catch {}
    finally { setLoading(false); }
  }, [isAuthenticated, api]);

  const syncGuestToServer = useCallback(async () => {
    const guest = readGuest();
    if (!guest.length || !api) return;
    for (const item of guest) {
      try {
        await api("/cart/items", {
          method: "POST",
          body: { product_id: item.product_id, quantity: item.quantity || 1 },
        });
      } catch {}
    }
    localStorage.removeItem(GUEST_KEY);
    await fetchServer();
  }, [api, fetchServer]);

  useEffect(() => {
    if (isAuthenticated) {
      const guest = readGuest();
      if (guest.length > 0) {
        syncGuestToServer();
      } else {
        fetchServer();
      }
    } else {
      setItems(readGuest());
    }
  }, [isAuthenticated, fetchServer, syncGuestToServer]);

  const addItem = useCallback(async (product, quantity = 1, resolvedPricing = null) => {
    if (isAuthenticated && api) {
      setUpdating(true);
      try {
        const res = await api("/cart/items", {
          method: "POST",
          body: { product_id: product.id, quantity },
        });
        if (res.ok) {
          await fetchServer();
          toast.success("Ajouté au panier");
          return true;
        } else {
          toast.error("Erreur lors de l'ajout");
          return false;
        }
      } catch { toast.error("Erreur réseau"); return false; }
      finally { setUpdating(false); }
    } else {
      const current = readGuest();
      const idx = current.findIndex((i) => i.product_id === product.id);
      const img = (product.images || []).find((u) => typeof u === "string" && u.startsWith("http")) || product.image || null;
      const resolvedPrice = resolvedPricing?.price != null ? Number(resolvedPricing.price) : null;
      const effectivePrice = resolvedPrice ?? (product.price != null ? Number(product.price) : null);
      if (idx >= 0) {
        current[idx].quantity = (current[idx].quantity || 1) + quantity;
        if (resolvedPrice != null) {
          current[idx].unit_price = effectivePrice;
          current[idx].price = effectivePrice;
        }
      } else {
        current.push({
          product_id: product.id,
          product_name: product.name,
          product_slug: product.slug,
          name: product.name,
          slug: product.slug,
          image: img,
          unit_price: effectivePrice,
          price: effectivePrice,
          quantity,
          has_visible_promo: resolvedPricing?.has_visible_promo || false,
          visible_promo_label: resolvedPricing?.visible_promo_label || null,
          price_before_visible_promo: resolvedPricing?.price_before_visible_promo != null
            ? Number(resolvedPricing.price_before_visible_promo) : null,
          price_type: resolvedPricing?.price_type || null,
        });
      }
      writeGuest(current);
      setItems([...current]);
      toast.success("Ajouté au panier");
      return true;
    }
  }, [isAuthenticated, api, fetchServer]);

  const removeItem = useCallback(async (productId) => {
    if (isAuthenticated && api) {
      setUpdating(true);
      try {
        const res = await api(`/cart/items/${productId}`, { method: "DELETE" });
        if (res.ok) await fetchServer();
      } catch {}
      finally { setUpdating(false); }
    } else {
      const current = readGuest().filter((i) => i.product_id !== productId);
      writeGuest(current);
      setItems(current);
    }
  }, [isAuthenticated, api, fetchServer]);

  const updateQuantity = useCallback(async (productId, quantity) => {
    if (quantity <= 0) return removeItem(productId);
    if (isAuthenticated && api) {
      setUpdating(true);
      try {
        const res = await api(`/cart/items/${productId}`, { method: "PUT", body: { quantity } });
        if (res.ok) await fetchServer();
      } catch {}
      finally { setUpdating(false); }
    } else {
      const current = readGuest();
      const idx = current.findIndex((i) => i.product_id === productId);
      if (idx >= 0) { current[idx].quantity = quantity; }
      writeGuest(current);
      setItems([...current]);
    }
  }, [isAuthenticated, api, fetchServer, removeItem]);

  const clearCart = useCallback(async () => {
    if (isAuthenticated && api) {
      setUpdating(true);
      try {
        const res = await api("/cart", { method: "DELETE" });
        if (res.ok) { setItems([]); }
      } catch {}
      finally { setUpdating(false); }
    } else {
      writeGuest([]);
      setItems([]);
    }
  }, [isAuthenticated, api]);

  const refreshGuestPrices = useCallback(async () => {
    if (isAuthenticated) return;
    const current = readGuest();
    if (!current.length) return;
    const pids = current.map((i) => i.product_id).filter(Boolean);
    if (!pids.length) return;
    try {
      const API_BASE = process.env.REACT_APP_BACKEND_URL || "http://localhost:8000";
      const res = await fetch(`${API_BASE}/api/v2/pricing/resolve-bulk`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ product_ids: pids }),
      });
      if (!res.ok) return;
      const data = await res.json();
      const results = data.results || {};
      let changed = false;
      const updated = current.map((item) => {
        const r = results[item.product_id];
        if (!r) return item;
        const newPrice = r.price != null ? Number(r.price) : null;
        if (newPrice != null && newPrice !== item.unit_price) changed = true;
        if (r.has_visible_promo !== item.has_visible_promo) changed = true;
        return {
          ...item,
          unit_price: newPrice ?? item.unit_price,
          price: newPrice ?? item.price,
          has_visible_promo: r.has_visible_promo || false,
          visible_promo_label: r.visible_promo_label || null,
          price_before_visible_promo: r.price_before_visible_promo != null ? Number(r.price_before_visible_promo) : null,
          price_type: r.price_type || null,
        };
      });
      if (changed) {
        writeGuest(updated);
        setItems([...updated]);
      }
    } catch {}
  }, [isAuthenticated]);

  return (
    <CartContext.Provider value={{ items, count, subtotal, loading, updating, addItem, removeItem, updateQuantity, clearCart, refresh: fetchServer, refreshGuestPrices }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be inside CartProvider");
  return ctx;
}
